
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface BirthdatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (date: Date) => void;
  currentDate?: Date;
}

const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember"
];

export const BirthdatePickerModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  currentDate 
}: BirthdatePickerModalProps) => {
  const [step, setStep] = useState<'year' | 'month' | 'day'>('year');
  const [selectedYear, setSelectedYear] = useState(currentDate?.getFullYear() || 1990);
  const [selectedMonth, setSelectedMonth] = useState(currentDate?.getMonth() || 0);
  const [selectedDay, setSelectedDay] = useState(currentDate?.getDate() || 1);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => currentYear - i);

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const daysInSelectedMonth = getDaysInMonth(selectedYear, selectedMonth);
  const days = Array.from({ length: daysInSelectedMonth }, (_, i) => i + 1);

  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    setStep('month');
  };

  const handleMonthSelect = (monthIndex: number) => {
    setSelectedMonth(monthIndex);
    // Adjust day if it's invalid for the selected month
    const maxDay = getDaysInMonth(selectedYear, monthIndex);
    if (selectedDay > maxDay) {
      setSelectedDay(maxDay);
    }
    setStep('day');
  };

  const handleDaySelect = (day: number) => {
    setSelectedDay(day);
  };

  const handleConfirm = () => {
    const date = new Date(selectedYear, selectedMonth, selectedDay);
    onSelect(date);
    onClose();
  };

  const handleBack = () => {
    if (step === 'month') setStep('year');
    if (step === 'day') setStep('month');
  };

  const renderStepContent = () => {
    switch (step) {
      case 'year':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-center">Jahr auswählen</h3>
            <div className="max-h-64 overflow-y-auto space-y-2 px-2">
              {years.map((year) => (
                <Button
                  key={year}
                  variant={year === selectedYear ? "default" : "outline"}
                  className="w-full justify-center"
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </Button>
              ))}
            </div>
          </div>
        );

      case 'month':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-center">
              Monat auswählen ({selectedYear})
            </h3>
            <div className="max-h-64 overflow-y-auto space-y-2 px-2">
              {MONTHS.map((month, index) => (
                <Button
                  key={month}
                  variant={index === selectedMonth ? "default" : "outline"}
                  className="w-full justify-center"
                  onClick={() => handleMonthSelect(index)}
                >
                  {month}
                </Button>
              ))}
            </div>
          </div>
        );

      case 'day':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-center">
              Tag auswählen ({MONTHS[selectedMonth]} {selectedYear})
            </h3>
            <div className="grid grid-cols-7 gap-2 max-h-64 overflow-y-auto px-2">
              {days.map((day) => (
                <Button
                  key={day}
                  variant={day === selectedDay ? "default" : "outline"}
                  size="sm"
                  className="aspect-square"
                  onClick={() => handleDaySelect(day)}
                >
                  {day}
                </Button>
              ))}
            </div>
          </div>
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Geburtsdatum auswählen</DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          {/* Step Indicator */}
          <div className="flex justify-center mb-6">
            <div className="flex space-x-2">
              {['year', 'month', 'day'].map((stepName, index) => (
                <div
                  key={stepName}
                  className={`w-3 h-3 rounded-full ${
                    step === stepName 
                      ? 'bg-uplivion-blue' 
                      : index < ['year', 'month', 'day'].indexOf(step)
                        ? 'bg-uplivion-blue/50'
                        : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <div className="flex space-x-2">
            {step !== 'year' && (
              <Button variant="outline" onClick={handleBack}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Zurück
              </Button>
            )}
          </div>
          
          <div className="flex space-x-2">
            <Button variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            {step === 'day' && (
              <Button onClick={handleConfirm} className="bg-uplivion-blue hover:bg-blue-700">
                Bestätigen
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
